/* ============================================================================
 * Royal Coffee — Invoice Payment helper
 *
 * Two responsibilities, picked by URL:
 *
 *   1. /pages/unpaid-invoices — decorate the existing unpaid-invoices table
 *      with checkboxes + editable per-invoice "amount to pay" inputs +
 *      an action bar directly beneath the invoice table. "Pay Now" creates a Shopify draft order
 *      via the Cloudflare Worker and redirects to the draft's invoiceUrl
 *      checkout page.
 *
 *   2. /cart — legacy hook (no-op now). Earlier design used cart line items;
 *      kept in case any old carts still contain them.
 *
 * Why draft orders: the previous `/cart/add.js + 1¢-per-unit` design hit
 * Shopify's 10,000-unit-per-line cap at $100/line. Draft orders accept any
 * line price + custom attributes and aren't subject to cart catalog rules.
 *
 * Endpoint: window.RC_INVOICE_WORKER_URL, with the existing public worker as
 * the default. Company payment eligibility controls are enforced by the theme.
 *
 * No jQuery, no framework. Vanilla, IE11-incompatible (template literals, etc).
 * ========================================================================== */

(function () {
  'use strict';

  var WORKER_BASE = (window.RC_INVOICE_WORKER_URL || 'https://royal-coffee-shipping.luna-7ff.workers.dev').replace(/\/$/, '');
  var CURRENCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

  // ----- helpers -----------------------------------------------------------

  function fmtUsd(cents) {
    var dollars = cents / 100;
    return dollars.toLocaleString('en-US', { style: 'currency', currency: CURRENCY });
  }

  // "$14,740.81" -> 1474081
  function parseDollarsToCents(str) {
    if (!str) return 0;
    var n = String(str).replace(/[^0-9.\-]/g, '');
    if (!n) return 0;
    return Math.round(parseFloat(n) * 100);
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      t = setTimeout(function () { fn.apply(null, args); }, ms);
    };
  }

  // ===========================================================================
  // 1) UNPAID INVOICES PAGE
  // ===========================================================================

  function initUnpaidInvoicesPage() {
    var section = document.querySelector('[data-portal-unpaid-invoices]');
    if (!section || section.getAttribute('data-invoice-payment-allowed') !== 'true') return;
    var table = section.querySelector('table.rc-portal__table');
    if (!table) return;
    var paymentPending = false;

    // Wait for the existing JS to render rows. The skeleton table starts with
    // a single placeholder <tr>, then real rows arrive asynchronously. Use a
    // MutationObserver to react when the row count changes.
    var observer = new MutationObserver(decorate);
    observer.observe(table, { childList: true, subtree: true });
    decorate();

    function decorate() {
      var thead = table.querySelector('thead tr');
      var tbody = table.querySelector('tbody');
      if (!thead || !tbody) return;

      // Header — add "Pay" + "Pay Amount" columns once
      if (!thead.querySelector('.rc-pay-col-head')) {
        var thSelect = document.createElement('th');
        thSelect.className = 'rc-pay-col-head';
        thSelect.innerHTML = '<input type="checkbox" aria-label="Select all invoices" data-rc-pay-select-all>';
        thead.insertBefore(thSelect, thead.firstChild);

        var thAmount = document.createElement('th');
        thAmount.className = 'rc-pay-amount-head';
        thAmount.textContent = 'Pay Amount';
        thead.appendChild(thAmount);

        var selectAll = thSelect.querySelector('input');
        selectAll.addEventListener('change', function () {
          tbody.querySelectorAll('tr[data-rc-invoice-row]').forEach(function (tr) {
            var cb = tr.querySelector('input[type="checkbox"][data-rc-pay-select]');
            if (cb && !cb.disabled) cb.checked = selectAll.checked;
          });
          updateActionBar();
        });
      }

      // For each data row, attach our checkbox and amount input (idempotent)
      var rows = tbody.querySelectorAll('tr');
      rows.forEach(function (tr) {
        if (tr.dataset.rcInvoiceRow) return;          // already decorated
        var cells = tr.querySelectorAll('td');
        if (cells.length < 5) return;                  // empty-state row

        var invoiceNumber = (cells[1] && cells[1].textContent || '').trim();
        var balanceCents = parseDollarsToCents(cells[3] && cells[3].textContent);
        if (!invoiceNumber || balanceCents <= 0) return;

        tr.dataset.rcInvoiceRow = '1';
        tr.dataset.invoiceNumber = invoiceNumber;
        tr.dataset.balanceCents = String(balanceCents);

        // Prepend checkbox cell
        var cbTd = document.createElement('td');
        cbTd.className = 'rc-pay-col';
        cbTd.innerHTML = '<input type="checkbox" data-rc-pay-select aria-label="Select invoice ' + invoiceNumber + '">';
        tr.insertBefore(cbTd, tr.firstChild);

        // Append editable amount input cell
        var amtTd = document.createElement('td');
        amtTd.className = 'rc-pay-amount-col';
        var input = document.createElement('input');
        input.type = 'text';
        input.inputMode = 'decimal';
        input.dataset.rcPayAmount = '';
        input.dataset.maxCents = String(balanceCents);
        input.value = fmtUsd(balanceCents).replace(/[^0-9.,]/g, '');
        input.setAttribute('aria-label', 'Pay amount for invoice ' + invoiceNumber);
        amtTd.appendChild(input);
        tr.appendChild(amtTd);

        // Wire events
        var checkbox = cbTd.querySelector('input');
        checkbox.addEventListener('change', updateActionBar);
        var validateAmount = debounce(function () {
          var cents = parseDollarsToCents(input.value);
          var max = parseInt(input.dataset.maxCents, 10);
          if (isNaN(cents) || cents < 1) cents = 1;
          if (cents > max) cents = max;
          input.value = (cents / 100).toFixed(2);
          updateActionBar();
        }, 300);
        input.addEventListener('input', updateActionBar);
        input.addEventListener('blur', validateAmount);
      });

      ensureActionBar();
      updateActionBar();
    }

    // ------ action bar ------------------------------------------------------

    function ensureActionBar() {
      if (section.querySelector('[data-rc-pay-action-bar]')) return;
      var bar = document.createElement('div');
      bar.setAttribute('data-rc-pay-action-bar', '');
      bar.className = 'rc-pay-bar';
      bar.innerHTML =
        '<div class="rc-pay-bar__inner">' +
          '<div class="rc-pay-bar__summary">' +
            '<span class="rc-pay-bar__count">0 invoices selected</span>' +
            '<span class="rc-pay-bar__total">$0.00</span>' +
          '</div>' +
          '<button type="button" class="rc-pay-bar__btn" disabled data-rc-pay-now>Pay Now</button>' +
        '</div>';
      // Keep payment controls with the invoices, independent of sidebar height.
      var tableWrapper = table.closest('.rc-portal__table-wrapper');
      if (!tableWrapper) return;
      tableWrapper.insertAdjacentElement('afterend', bar);
      bar.querySelector('[data-rc-pay-now]').addEventListener('click', payNow);
    }

    function getSelected() {
      var items = [];
      table.querySelectorAll('tr[data-rc-invoice-row]').forEach(function (tr) {
        var cb = tr.querySelector('input[type="checkbox"][data-rc-pay-select]');
        if (!cb || !cb.checked) return;
        var input = tr.querySelector('input[data-rc-pay-amount]');
        var cents = parseDollarsToCents(input && input.value);
        var max = parseInt(tr.dataset.balanceCents, 10);
        if (cents < 1) cents = 1;
        if (cents > max) cents = max;
        items.push({
          invoiceNumber: tr.dataset.invoiceNumber,
          amountCents: cents,
          balanceCents: max,
        });
      });
      return items;
    }

    function updateActionBar() {
      var bar = section.querySelector('[data-rc-pay-action-bar]');
      if (!bar) return;
      var sel = getSelected();
      var totalCents = sel.reduce(function (s, it) { return s + it.amountCents; }, 0);
      bar.querySelector('.rc-pay-bar__count').textContent =
        sel.length + ' invoice' + (sel.length === 1 ? '' : 's') + ' selected';
      bar.querySelector('.rc-pay-bar__total').textContent = fmtUsd(totalCents);
      bar.querySelector('[data-rc-pay-now]').disabled = paymentPending || sel.length === 0;
    }

    function payNow() {
      if (paymentPending) return;
      if (section.getAttribute('data-invoice-payment-allowed') !== 'true') return;
      var btn = section.querySelector('[data-rc-pay-now]');
      if (!btn) return;
      var sel = getSelected();
      if (sel.length === 0) return;

      paymentPending = true;
      btn.disabled = true;
      btn.textContent = 'Creating checkout…';

      // POST to the Cloudflare Worker, which creates a Shopify draft order
      // (one custom line item per invoice) and returns its invoiceUrl. The
      // customer pays through that URL. No cart, no quantity caps.
      var customerEmail = (window.Shopify && window.Shopify.customer && window.Shopify.customer.email)
        || (window.RC_CUSTOMER_EMAIL || undefined);
      var payload = {
        items: sel.map(function (it) {
          return {
            invoiceNumber: it.invoiceNumber,
            amountCents:   it.amountCents,
            balanceCents:  it.balanceCents,
          };
        }),
        customerEmail: customerEmail,
      };

      fetch(WORKER_BASE + '/create-invoice-draft', {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      })
      .then(function (r) {
        return r.text().then(function (body) {
          var j;
          try {
            j = JSON.parse(body);
          } catch (e) {
            throw new Error('Invoice payment service is unavailable (HTTP ' + r.status + '). Please contact customer service.');
          }
          if (!r.ok || !j || j.ok === false) {
            var msg = (j && (j.error || j.message)) || ('Server error ' + r.status);
            if (j && j.details) {
              try { msg += ': ' + (typeof j.details === 'string' ? j.details : JSON.stringify(j.details)); } catch (e) {}
            }
            throw new Error(msg);
          }
          return j;
        });
      })
      .then(function (data) {
        if (!data.invoice_url) throw new Error('No invoice URL returned');
        window.location.href = data.invoice_url;
      })
      .catch(function (err) {
        console.error('[invoice-pay]', err);
        paymentPending = false;
        btn.textContent = 'Pay Now';
        updateActionBar();
        alert('Could not create invoice payment: ' + err.message);
      });
    }
  }

  // ===========================================================================
  // 2) CART PAGE — relabel + lock invoice-payment lines
  // ===========================================================================

  function initCartPage() {
    if (!/^\/cart(\/|$|\?)/.test(window.location.pathname)) return;

    var apply = function () {
      var lines = document.querySelectorAll('[data-line-item], .cart-item, [class*="cart-item"]');
      lines.forEach(rewriteLine);
    };

    apply();
    // Most themes update the cart in place after qty changes etc; observe.
    var observer = new MutationObserver(debounce(apply, 100));
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function rewriteLine(line) {
    if (line.dataset.rcInvoicePayProcessed) return;
    var text = line.textContent || '';
    if (text.indexOf('Invoice Number') === -1) return;     // not an invoice line
    line.dataset.rcInvoicePayProcessed = '1';

    // Pull the invoice number + amount from the rendered properties block.
    // Most Dawn-derived themes render properties as <dl><dt>Invoice Number</dt><dd>...</dd></dl>
    // or as plain "Property: value" lines. We'll try both.
    var invoiceNumber = '';
    var amountPaid = '';
    var paymentType = '';

    var dts = line.querySelectorAll('dt, dd, [class*="property"]');
    dts.forEach(function (n) {
      var t = (n.textContent || '').trim();
      if (/Invoice Number/i.test(t) && n.nextSibling) {
        invoiceNumber = (n.nextSibling.textContent || '').trim() || (n.nextElementSibling && n.nextElementSibling.textContent || '').trim();
      }
      var m;
      if ((m = t.match(/Invoice Number[:\s]+([0-9A-Z\-]+)/i))) invoiceNumber = m[1];
      if ((m = t.match(/Amount Paid[:\s]+\$?([0-9.,]+)/i))) amountPaid = '$' + m[1];
      if ((m = t.match(/Payment Type[:\s]+(\w+)/i))) paymentType = m[1];
    });

    line.classList.add('rc-invoice-pay-line');
    if (invoiceNumber) line.dataset.rcInvoiceNumber = invoiceNumber;

    // Lock quantity: hide quantity input/buttons inside this line.
    var qtyControls = line.querySelectorAll('input[name="updates[]"], input[name*="quantity"], button[name*="minus"], button[name*="plus"], .quantity, [class*="quantity"]');
    qtyControls.forEach(function (el) {
      // Don't hide the remove link — its container often shares a class
      if (el.matches('a[href*="quantity=0"], button[name*="remove"], a[class*="remove"]')) return;
      el.style.display = 'none';
    });
  }

  // ===========================================================================
  // boot
  // ===========================================================================

  function injectStyles() {
    if (document.getElementById('rc-invoice-pay-styles')) return;
    var css = [
      '.rc-pay-col, .rc-pay-col-head { width: 36px; text-align: center; }',
      '.rc-pay-col input[type="checkbox"], .rc-pay-col-head input[type="checkbox"] { transform: scale(1.2); cursor: pointer; }',
      '.rc-pay-amount-col { width: 120px; }',
      '.rc-pay-amount-col input { width: 100%; padding: 6px 8px; border: 1px solid #ccc; border-radius: 4px; text-align: right; font-variant-numeric: tabular-nums; }',
      '.rc-pay-amount-col input:focus { outline: 2px solid #D62428; outline-offset: 1px; }',
      '.rc-pay-bar { margin-top: 16px; background: #1D1D1D; color: #fff; padding: 16px 24px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 8px; }',
      '.rc-pay-bar__inner { display: flex; align-items: center; justify-content: space-between; gap: 24px; max-width: 1200px; margin: 0 auto; }',
      '.rc-pay-bar__summary { display: flex; flex-direction: column; gap: 2px; }',
      '.rc-pay-bar__count { font-size: 14px; opacity: 0.85; }',
      '.rc-pay-bar__total { font-size: 24px; font-weight: 700; font-variant-numeric: tabular-nums; }',
      '.rc-pay-bar__btn { padding: 12px 28px; background: #D62428; color: #fff; border: 0; border-radius: 6px; font-size: 16px; font-weight: 600; cursor: pointer; transition: background 0.15s; }',
      '.rc-pay-bar__btn:hover:not(:disabled) { background: #b51e22; }',
      '.rc-pay-bar__btn:disabled { background: #555; cursor: not-allowed; opacity: 0.6; }',
      '@media (max-width: 600px) { .rc-pay-bar__inner { flex-direction: column; align-items: stretch; gap: 12px; } .rc-pay-bar__btn { width: 100%; } }',
      '.rc-invoice-pay-line { /* hooks for any further theme-specific tweaks */ }',
    ].join('\n');
    var s = document.createElement('style');
    s.id = 'rc-invoice-pay-styles';
    s.textContent = css;
    document.head.appendChild(s);
  }

  function boot() {
    injectStyles();
    if (window.location.pathname.indexOf('/pages/unpaid-invoices') === 0) {
      initUnpaidInvoicesPage();
    }
    initCartPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
